import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { clearViewer } from '@/entities/viewer';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, ConfirmDialog, ErrorState, LoadingState } from '@/shared/ui';
import { useQueryClient } from '@tanstack/react-query';

import { type AccountSession, useRevokeOtherSessions, useRevokeSession, useSessions } from './api';
import styles from './SessionsCard.module.css';
import { describeUserAgent } from './user-agent';

type Pending = { kind: 'one'; session: AccountSession } | { kind: 'others' } | null;

/**
 * Settings -> Security -> Active sessions: the user's live sessions with an approximate device
 * label (browser and system from the User-Agent; nothing is guessed beyond that), when each was
 * created and when it expires. Revoking is server-side. Revoking the CURRENT session (after a
 * confirmation that says so) signs this browser out.
 */
export function SessionsCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const [pending, setPending] = useState<Pending>(null);
  const rows = sessions.data ?? [];
  const others = rows.filter((row) => !row.is_current).length;

  const confirm = () => {
    if (!pending) return;
    const done = () => {
      setPending(null);
    };
    if (pending.kind === 'others') {
      revokeOthers.mutate(undefined, { onSettled: done });
      return;
    }
    const { session } = pending;
    revoke.mutate(session.id, {
      onSuccess: () => {
        if (session.is_current) {
          clearViewer(client);
          void navigate('/login', { replace: true });
        }
      },
      onSettled: done,
    });
  };

  return (
    <>
      {sessions.isPending ? (
        <LoadingState />
      ) : sessions.isError ? (
        <ErrorState
          error={sessions.error}
          onRetry={() => {
            void sessions.refetch();
          }}
        />
      ) : (
        <>
          <p className={styles.note}>{t('account.sessions.note')}</p>
          <ul className={styles.list}>
            {rows.map((row) => (
              <li key={row.id} className={styles.row} data-current={row.is_current || undefined}>
                <div className={styles.info}>
                  <span className={styles.device}>
                    {describeUserAgent(row.user_agent) ?? t('account.sessions.unknown')}
                    {row.is_current && (
                      <span className={styles.badge}>{t('account.sessions.current')}</span>
                    )}
                  </span>
                  <span className={styles.meta} data-testid="session-activity">
                    {t('account.sessions.created', {
                      date: formatDateTime(row.created_at, i18n.language),
                    })}
                    {' · '}
                    {row.last_active_at
                      ? t('account.sessions.lastActive', {
                          date: formatDateTime(row.last_active_at, i18n.language),
                        })
                      : t('account.sessions.lastActiveUnknown')}
                    {' · '}
                    {t('account.sessions.expires', {
                      date: formatDateTime(row.expires_at, i18n.language),
                    })}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant={row.is_current ? 'ghost' : 'secondary'}
                  onClick={() => {
                    setPending({ kind: 'one', session: row });
                  }}
                >
                  {row.is_current
                    ? t('account.sessions.signOutHere')
                    : t('account.sessions.revoke')}
                </Button>
              </li>
            ))}
          </ul>
          {others > 0 && (
            <div>
              <Button
                onClick={() => {
                  setPending({ kind: 'others' });
                }}
              >
                {t('account.sessions.revokeOthers', { count: others })}
              </Button>
            </div>
          )}
          {others === 0 && <p className={styles.note}>{t('account.sessions.onlyThis')}</p>}
        </>
      )}
      {pending && (
        <ConfirmDialog
          title={
            pending.kind === 'others'
              ? t('account.sessions.confirmOthersTitle')
              : pending.session.is_current
                ? t('account.sessions.confirmCurrentTitle')
                : t('account.sessions.confirmOneTitle')
          }
          message={
            pending.kind === 'others'
              ? t('account.sessions.confirmOthers', { count: others })
              : pending.session.is_current
                ? t('account.sessions.confirmCurrent')
                : t('account.sessions.confirmOne')
          }
          confirmLabel={
            pending.kind === 'one' && pending.session.is_current
              ? t('account.sessions.signOutHere')
              : t('account.sessions.revokeConfirm')
          }
          pending={revoke.isPending || revokeOthers.isPending}
          onConfirm={confirm}
          onCancel={() => {
            setPending(null);
          }}
        />
      )}
    </>
  );
}
